import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { FanMembership, FanStatus } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Heading } from '../../ui/heading';
import { Text } from '../../ui/text';
import { EmptyState, StatCard } from '../../components/PageChrome';

const STATUS_FILTERS: (FanStatus | 'ALL')[] = ['ALL', 'PENDING', 'APPROVED', 'DENIED'];

const statusLabel = (status: FanStatus | 'ALL') =>
  status === 'ALL' ? 'All' : `${status.charAt(0)}${status.slice(1).toLowerCase()}`;

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const badgeColor = (s: FanStatus): 'amber' | 'lime' | 'rose' =>
  s === 'PENDING' ? 'amber' : s === 'APPROVED' ? 'lime' : 'rose';

export default function Fans() {
  const { teamId } = useParams<{ teamId: string }>();

  const [fans, setFans] = useState<FanMembership[]>([]);
  const [filter, setFilter] = useState<FanStatus | 'ALL'>('ALL');
  const [error, setError] = useState<string>();

  const load = async () => {
    if (!teamId) return;
    try {
      const res = await admin.listFans(teamId);
      setFans(res.fans);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const visible = useMemo(
    () =>
      filter === 'ALL'
        ? fans
        : fans.filter((f) => f.status === filter),
    [fans, filter],
  );

  const counts = useMemo(() => {
    const c = { PENDING: 0, APPROVED: 0, DENIED: 0 };
    for (const f of fans) c[f.status]++;
    return c;
  }, [fans]);

  const act = async (
    fn: (teamId: string, userId: string) => Promise<void>,
    fan: FanMembership,
  ) => {
    if (!teamId) return;
    try {
      await fn(teamId, fan.userId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Fans</Heading>
          <Text className="mt-1 text-sm">
            Review follow requests and keep the approved family audience clean.
          </Text>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Pending" value={counts.PENDING} tone="amber" />
        <StatCard label="Approved" value={counts.APPROVED} tone="emerald" />
        <StatCard label="Denied" value={counts.DENIED} />
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={
              filter === f
                ? 'rounded-lg bg-zinc-100 px-3 py-2 font-medium text-zinc-900'
                : 'rounded-lg border border-zinc-700 px-3 py-2 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100'
            }
          >
            {f === 'ALL' ? `All (${fans.length})` : `${statusLabel(f)} (${counts[f]})`}
          </button>
        ))}
      </div>

      {error ? <Text className="text-red-400">{error}</Text> : null}

      {visible.length === 0 ? (
        <EmptyState
          title={filter === 'ALL' ? 'No fans yet' : `No ${statusLabel(filter).toLowerCase()} fans`}
          body={
            filter === 'ALL'
              ? 'New follow requests show up here after families sign in and request access.'
              : `There are no fan memberships currently marked ${statusLabel(filter).toLowerCase()}.`
          }
        />
      ) : (
        <ul className="grid min-w-0 gap-3 lg:grid-cols-2">
          {visible.map((f) => (
            <li
              key={f.userId}
              className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="break-all font-medium text-zinc-100">
                    {f.email ?? f.userId}
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">
                    Requested {fmtDate(f.requestedAt)}
                    {f.approvedAt ? ` · Decided ${fmtDate(f.approvedAt)}` : ''}
                  </div>
                  <Badge color={badgeColor(f.status)} className="mt-3">
                    {f.status}
                  </Badge>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                  {f.status !== 'APPROVED' ? (
                    <Button
                      plain
                      className="text-emerald-400"
                      onClick={() => void act(admin.approveFan, f)}
                    >
                      Approve
                    </Button>
                  ) : null}
                  {f.status !== 'DENIED' ? (
                    <Button
                      plain
                      className="text-zinc-300"
                      onClick={() => void act(admin.denyFan, f)}
                    >
                      Deny
                    </Button>
                  ) : null}
                  <Button
                    plain
                    className="text-red-400"
                    onClick={() => {
                      if (window.confirm(`Remove ${f.email ?? f.userId}?`)) {
                        void act(admin.removeFan, f);
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
